import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Rozwijana lista z wyszukiwaniem (combobox) — drop-in dla natywnego <select>.
 *
 * Można podać opcje na dwa sposoby (mechaniczna migracja z <select>):
 *  1. jako `children` <option>/<optgroup> (tak jak w <select>) — najprostsza podmiana,
 *  2. jako tablicę `options`.
 *
 * Różnice względem <select>:
 *  - `onChange` dostaje bezpośrednio wartość (string), nie event.
 *    <select onChange={e => setX(e.target.value)}>  →  onChange={setX}
 *    numeryczne:  onChange={v => setX(Number(v))}
 *  - wpisanie tekstu filtruje pozycje (bez uwzględniania wielkości liter i polskich znaków).
 */
export interface SelectOption {
  value: string;
  label: string;
  group?: string;
  disabled?: boolean;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options?: SelectOption[];
  children?: React.ReactNode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  title?: string;
  searchPlaceholder?: string;
  /** Pole wyszukiwania pokazuje się gdy liczba opcji > tego progu. Domyślnie 0 = zawsze (każda lista
   *  ma szukajkę). Nawet gdy pole jest ukryte (wyższy próg), lista i tak filtruje się przy pisaniu. */
  searchThreshold?: number;
  /** Wymuś/wyłącz krzyżyk „wyczyść". Domyślnie: pokazuje się gdy wybrano wartość i istnieje opcja pusta (''). */
  clearable?: boolean;
  /** Pokaż aktualną wartość nawet gdy nie ma jej na liście opcji (np. gmina z geokodowania). */
  allowFreeValue?: boolean;
  /** Lista słownikowa z możliwością dopisania: gdy wpisany tekst nie pasuje dokładnie do żadnej pozycji,
   *  na końcu listy pojawia się „Dodaj „…””, a wybranie go przekazuje wpisany tekst jako wartość.
   *  Zwykle razem z `allowFreeValue`, żeby dopisana wartość była widoczna po zamknięciu listy. */
  allowCreate?: boolean;
}

/** Normalizacja do wyszukiwania: bez wielkości liter i bez znaków diakrytycznych (ó→o, ą→a…). */
const norm = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

function nodeText(node: React.ReactNode): string {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (React.isValidElement(node)) return nodeText((node.props as { children?: React.ReactNode }).children);
  return '';
}

/** Zamienia <option>/<optgroup> children na model opcji. */
function optionsFromChildren(children: React.ReactNode): SelectOption[] {
  const out: SelectOption[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const el = child as React.ReactElement<{ label?: string; value?: unknown; disabled?: boolean; children?: React.ReactNode }>;
    if (el.type === 'optgroup') {
      const group = el.props.label;
      React.Children.forEach(el.props.children, (opt) => {
        if (React.isValidElement(opt) && opt.type === 'option') {
          const o = opt as React.ReactElement<{ value?: unknown; disabled?: boolean; children?: React.ReactNode }>;
          out.push({ value: String(o.props.value ?? ''), label: nodeText(o.props.children), group, disabled: o.props.disabled });
        }
      });
    } else if (el.type === 'option') {
      out.push({ value: String(el.props.value ?? ''), label: nodeText(el.props.children), disabled: el.props.disabled });
    }
  });
  return out;
}

export function SearchableSelect({
  value, onChange, options, children,
  placeholder = '— wybierz —', className = '', disabled, id, title,
  searchPlaceholder = 'Szukaj…', searchThreshold = 0, clearable, allowFreeValue, allowCreate,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const allOptions = useMemo(
    () => options ?? optionsFromChildren(children),
    [options, children],
  );
  const selected = allOptions.find(o => o.value === value) ?? null;
  // allowFreeValue: pokaż aktualną wartość nawet gdy nie ma jej na liście (np. gmina z geokodowania,
  // której format nie pasuje 1:1 do słownika). W przeciwnym razie wartość-spoza-listy → placeholder.
  const displayLabel = selected ? selected.label : (allowFreeValue && value ? value : null);

  // Pole wyszukiwania tylko dla dłuższych list (krótkie enumy nie potrzebują szukajki).
  const showSearch = allOptions.length > searchThreshold;
  // Krzyżyk „wyczyść": gdy jest niepusta wartość i albo istnieje opcja pusta (''), albo wymuszono clearable.
  const hasEmptyOption = allOptions.some(o => o.value === '');
  const canClear = (clearable ?? hasEmptyOption) && !!value && value !== '' && !disabled;

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return allOptions;
    const hits = allOptions.filter(o => norm(o.label).includes(q));
    if (allowCreate && !allOptions.some(o => norm(o.value) === q || norm(o.label) === q)) {
      hits.push({ value: query.trim(), label: `Dodaj „${query.trim()}”` });
    }
    return hits;
  }, [allOptions, query, allowCreate]);

  // Zamknięcie po kliknięciu poza komponentem.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Po otwarciu: wyczyść filtr, ustaw kursor na zaznaczonej pozycji, sfokusuj pole wyszukiwania.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    const idx = allOptions.findIndex(o => o.value === value);
    setActiveIdx(idx >= 0 ? idx : 0);
    // Na urządzeniach dotykowych nie fokusujemy pola wyszukiwania — inaczej otwarcie krótkiej listy
    // wywołałoby klawiaturę ekranową zasłaniającą opcje. Użytkownik może dotknąć pola, gdy chce szukać.
    const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
    const raf = requestAnimationFrame(() => {
      if (showSearch && !coarse) inputRef.current?.focus();
      else if (!showSearch) listRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wpisywanie tekstu przewija na pierwszy wynik.
  useEffect(() => { if (query) setActiveIdx(0); }, [query]);

  // Utrzymanie widoczności aktywnej pozycji.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const commit = (opt: SelectOption | undefined) => {
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); commit(filtered[activeIdx]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };

  // Type-ahead dla list bez widocznej szukajki (wyższy searchThreshold): pisanie filtruje listę,
  // Backspace kasuje ostatni znak — dzięki temu KAŻDA lista ogranicza się podczas wpisywania.
  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') { e.preventDefault(); setQuery(q => q.slice(0, -1)); return; }
    // Dopuść zwykłe znaki oraz AltGr (Ctrl+Alt — polskie znaki na układzie „programisty").
    // Pomiń skróty Ctrl/Meta (np. Ctrl+C), żeby ich nie przechwytywać.
    const isShortcut = e.metaKey || (e.ctrlKey && !e.altKey);
    if (e.key.length === 1 && !isShortcut) {
      e.preventDefault(); setQuery(q => q + e.key); return;
    }
    onSearchKeyDown(e);
  };

  let lastGroup: string | undefined;

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button" id={id} disabled={disabled} title={title}
        aria-haspopup="listbox" aria-expanded={open}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (open && e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true); }
        }}
        className={'select flex items-center justify-between gap-2 text-left '
          + (disabled ? 'opacity-60 cursor-not-allowed ' : 'cursor-pointer ') + className}
      >
        <span className={'truncate ' + (displayLabel ? 'text-ink-800' : 'text-ink-400')}>
          {displayLabel ?? placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {canClear && (
            <span role="button" tabIndex={-1} aria-label="Wyczyść" title="Wyczyść"
                  onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onChange(''); setOpen(false); }}
                  className="text-ink-300 hover:text-ink-600 cursor-pointer">
              <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 6l8 8M14 6l-8 8" strokeLinecap="round"/>
              </svg>
            </span>
          )}
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8"
               className={'text-ink-400 transition-transform ' + (open ? 'rotate-180' : '')}>
            <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[12rem] bg-white border border-ink-200 rounded-md shadow-lg">
          {showSearch && (
            <div className="p-1.5 border-b border-ink-100">
              <input
                ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onSearchKeyDown}
                placeholder={searchPlaceholder}
                className="w-full text-[13px] px-2 py-1.5 border border-ink-200 rounded outline-none focus:border-water-500 focus:ring-2 focus:ring-water-500/20"
              />
            </div>
          )}
          {!showSearch && query && (
            <div className="px-3 py-1 border-b border-ink-100 text-[11px] text-ink-500 flex items-center gap-1">
              <span className="text-ink-400">Filtr:</span>
              <span className="font-medium text-ink-700">{query}</span>
            </div>
          )}
          <div ref={listRef} role="listbox"
               tabIndex={showSearch ? -1 : 0}
               onKeyDown={showSearch ? undefined : onListKeyDown}
               className="max-h-[240px] overflow-auto py-1 outline-none">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-[12px] text-ink-400">Brak wyników</div>
            )}
            {filtered.map((o, idx) => {
              const showGroup = o.group && o.group !== lastGroup;
              lastGroup = o.group;
              const isActive = idx === activeIdx && !o.disabled;
              const isSelected = o.value === value;
              return (
                <div key={o.value + '::' + idx}>
                  {showGroup && (
                    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-400">{o.group}</div>
                  )}
                  <div
                    data-idx={idx} role="option" aria-selected={isSelected}
                    onMouseEnter={() => !o.disabled && setActiveIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); commit(o); }}
                    className={'px-3 py-1.5 text-[13px] flex items-center justify-between gap-2 '
                      + (o.disabled ? 'text-ink-300 cursor-not-allowed ' : 'cursor-pointer ')
                      + (isActive ? 'bg-water-50 ' : '')
                      + (isSelected ? 'font-semibold text-ink-900' : (o.disabled ? '' : 'text-ink-700'))}
                  >
                    <span className="truncate">{o.label}</span>
                    {isSelected && (
                      <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
                           className="shrink-0 text-water-600">
                        <path d="M5 10l4 4 6-8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
